import * as React from "react";
import { cn } from "../lib/utils.ts";

export interface PriceCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  price: string;
  currency: string;
  periodLabel: string;
  features?: string[];
  cta: React.ReactNode;
  highlighted?: boolean;
  badge?: string;
}

export const PriceCard = React.forwardRef<HTMLDivElement, PriceCardProps>(
  (
    { label, price, currency, periodLabel, features, cta, highlighted, badge, className, ...props },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "relative flex flex-col gap-6 rounded-2xl border bg-[var(--color-card)] p-6 transition-transform md:p-8",
          highlighted
            ? "border-[var(--color-border)] ring-1 ring-[var(--color-accent)]/40 md:scale-[1.02]"
            : "border-[var(--color-border)]",
          className,
        )}
        {...props}
      >
        {badge && (
          <span className="absolute -top-3 right-6 rounded-full bg-[var(--color-accent)]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/30">
            {badge}
          </span>
        )}
        <div>
          <p className="text-sm uppercase tracking-[0.15em] text-[var(--color-muted-foreground)]">
            {label}
          </p>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-5xl font-bold tabular tracking-tight" data-tabular>
              {price}
            </span>
            <span className="text-base text-[var(--color-muted-foreground)]">{currency}</span>
            <span className="text-base text-[var(--color-muted-foreground)]">/ {periodLabel}</span>
          </div>
        </div>

        {features && features.length > 0 && (
          <ul className="space-y-2 text-sm">
            {features.map((feature) => (
              <li key={feature} className="flex items-start gap-2">
                <span aria-hidden className="mt-1 text-[var(--color-accent)]">
                  ✓
                </span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-auto">{cta}</div>
      </div>
    );
  },
);
PriceCard.displayName = "PriceCard";
